import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { Button, Col, Form, Popconfirm, Row, Steps, StepsProps } from "antd";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";

import { useCreateProperty } from "hooks/useCreateProperty";
import { useGetPropertiesUsages } from "hooks/useGetPropertiesUsages";
import { useUpdateProperty } from "hooks/useUpdateProperty";

import LandlordTab from "js/components/Properties/PropertyWithLandlordModal/LandlordTab";
import PropertyTab from "js/components/Properties/PropertyWithLandlordModal/PropertyTab";
import { PropertyFormProps } from "js/components/Properties/types";
import { Property } from "js/components/types";

import "styles/pages/Properties/PropertyForm.scss";

const PropertyLandlordForm = forwardRef<any, PropertyFormProps>(
  (props, ref) => {
    const {
      propertyId,
      setSelectedProperty,
      propertyData,
      setPropertyData,
      messageApi,
      handleClose,
    } = props;
    const screen = useBreakpoint();
    const [form] = Form.useForm();
    const [step, setStep] = useState(0);
    const [propertyDataForm, setPropertyDataForm] = useState<any | null>({});
    const [selectedLandlord, setSelectedLandlord] = React.useState({});
    const [isEditView, setIsEditView] = useState(false);
    const [isCreateView, setIsCreateView] = useState(false);
    const [resetKey, setResetKey] = useState(0);

    // Fetch properties usages for dropdown
    const { data: propertiesUsagesData = [] } = useGetPropertiesUsages();
    const propertiesUsages = propertiesUsagesData.map((usage: any) => ({
      label: usage.usage,
      value: usage.usage,
    }));

    const createPropertyMutation = useCreateProperty();
    const updatePropertyMutation = useUpdateProperty();

    // Combined loading state from mutations
    const loading =
      createPropertyMutation.isPending || updatePropertyMutation.isPending;

    useImperativeHandle(ref, () => ({
      resetForm: () => {
        setStep(0);
        if (!propertyId) {
          form.resetFields();
          setResetKey((k) => k + 1);
        }
        setIsEditView(false);
        setIsCreateView(false);
      },
    }));

    const stepItems: StepsProps["items"] = [
      {
        title: "Inmueble",
      },
      {
        title: "Propietario",
      },
    ];

    const onFinish = () => {
      const {
        street,
        neighborhood,
        exterior_number,
        interior_number,
        zip_code,
        city,
        state,
        municipality,
        rental_price,
        maintenance_price,
        alias,
        usage: usageValue,
      } = propertyDataForm;
      const address = {
        street,
        neighborhood,
        exterior_number,
        interior_number,
        zip_code,
        city,
        state,
        municipality,
      };
      const propertyUsage = propertiesUsages.find((usage) =>
        propertyId ? usage.label === usageValue : usage.value === usageValue,
      );

      const newProperty: Property = {
        rental_price: Number(rental_price),
        maintenance_price: maintenance_price || 0,
        alias,
        usage: propertyUsage?.label,
        address,
        landlord_id:
          Object.keys(selectedLandlord).length > 0 ? selectedLandlord.id : null,
      };

      if (propertyId) {
        newProperty.id = propertyId;
        updatePropertyMutation.mutate(
          { property: newProperty, propertyId },
          {
            onSuccess: (property) => {
              setPropertyData(
                propertyData.map((item) =>
                  item.id === propertyId ? { ...item, ...property } : item,
                ),
              );
              messageApi.success(
                "Se ha actualizado correctamente la información del inmueble",
              );
              setStep(0);
              handleClose();
            },
            onError: () => {
              messageApi.error("Algo salió mal, inténtelo de nuevo.");
            },
          },
        );
      } else {
        createPropertyMutation.mutate(newProperty, {
          onSuccess: (propertyResponse) => {
            if (setSelectedProperty) {
              setSelectedProperty(propertyResponse);
            }
            setPropertyData([...propertyData, propertyResponse]);
            messageApi.success("Se ha creado correctamente el inmueble");
            setStep(0);
            handleClose();
          },
          onError: () => {
            messageApi.error("Algo salió mal, inténtelo de nuevo.");
          },
        });
      }
    };

    const onFinishFailed = () => {
      messageApi.error("Por favor completa los datos del inmueble.");
    };

    const handleStepClick = async (step: number) => {
      if (step === 1) {
        await form.validateFields();
        setStep(step);
      } else if (step === 0) {
        setStep(step);
        setIsEditView(false);
        setIsCreateView(false);
      }
    };

    const handleBackClick = () => {
      if (step === 0) {
        handleClose();
        setIsEditView(false);
        setIsCreateView(false);
      } else {
        setStep((prev) => prev - 1);
      }
    };

    const handleFollowingClick = async () => {
      if (step === 0) {
        const formValues = await form.validateFields();
        setPropertyDataForm(formValues);
        setStep((prev) => prev + 1);
      } else if (step === 1) {
        form.submit();
      }
    };

    const getFormByStep = (step: number) => {
      switch (step) {
        case 0:
          return (
            <PropertyTab
              key={resetKey}
              form={form}
              propertiesUsages={propertiesUsages}
              propertyId={propertyId}
            />
          );
        case 1:
          return (
            <Row style={{ width: "100%" }}>
              <LandlordTab
                selectedLandlord={selectedLandlord}
                setSelectedLandlord={setSelectedLandlord}
                isEditView={isEditView}
                isCreateView={isCreateView}
                setIsEditView={setIsEditView}
                setIsCreateView={setIsCreateView}
              />
            </Row>
          );
        default:
          return null;
      }
    };

    useEffect(() => {
      if (propertyId) {
        const data = propertyData.find(
          (property) => property.id === propertyId,
        );
        const filteredPropertyData = {
          ...data,
          ...data?.address,
        };
        setSelectedLandlord(filteredPropertyData?.landlord ?? {});
        form.setFieldsValue(filteredPropertyData);
      } else {
        form.resetFields();
        setSelectedLandlord({});
      }
    }, [propertyId]);

    return (
      <>
        <Row justify="center">
          <Col xs={24} sm={20} md={16} lg={12}>
            <Steps
              labelPlacement="vertical"
              size="small"
              current={step}
              items={stepItems.map((item, index) => ({
                ...item,
                onClick: () => handleStepClick(index),
                style: { cursor: "pointer" },
              }))}
              style={{ width: "100%" }}
            />
          </Col>
        </Row>
        <Row style={{ width: "100%", marginTop: 15, marginBottom: 10 }}>
          <Form
            form={form}
            name="propertyForm"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            layout="vertical"
            style={{ width: "100%" }}
          >
            {getFormByStep(step)}
          </Form>
        </Row>
        {!isEditView && !isCreateView && (
          <Row
            gutter={{ xs: 8, sm: 16, md: 12, lg: 12 }}
            style={{
              height: "100%",
              width: "100%",
              display: "flex",
              alignItems: "end",
              marginTop: !screen.lg && step === 1 ? 15 : 0,
            }}
          >
            <Col
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              style={{ marginTop: 10, textAlign: "center" }}
            >
              <Button onClick={handleBackClick} disabled={loading}>
                {step === 0 ? "Cancelar" : "Regresar"}
              </Button>
            </Col>
            <Col
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              style={{ textAlign: "center" }}
            >
              {step === 0 ? (
                <Form.Item style={{ margin: 0 }}>
                  <Button
                    loading={loading}
                    type={"primary"}
                    onClick={handleFollowingClick}
                  >
                    Siguiente
                  </Button>
                </Form.Item>
              ) : Object.keys(selectedLandlord).length > 0 ? (
                <Form.Item style={{ margin: 0 }}>
                  <Button
                    loading={loading}
                    type={"primary"}
                    onClick={handleFollowingClick}
                  >
                    Registrar Inmueble
                  </Button>
                </Form.Item>
              ) : (
                <Popconfirm
                  title="Inmueble sin propietario"
                  description="¿Desea guardar la información del inmueble sin propietario?"
                  placement="top"
                  onConfirm={handleFollowingClick}
                >
                  <Form.Item style={{ margin: 0 }}>
                    <Button loading={loading} type={"primary"}>
                      Registrar Inmueble
                    </Button>
                  </Form.Item>
                </Popconfirm>
              )}
            </Col>
          </Row>
        )}
      </>
    );
  },
);

export default PropertyLandlordForm;
